import UZod from "@repo/utils/UZod";
import Customer from "./Customer.js";
import Organization from "./Organization.js";
import Case from "@repo/utils/Case";
import Duration from "@repo/utils/Duration";
import UObject from "@repo/utils/UObject";
import UString from "@repo/utils/UString";
import { z } from "zod";

type Membership = {
  id: Membership.Id;
  expiresAt: Date;
  activatedAt: Date;
  planId: Membership.Plan.Id;
  customerId: Customer.Id;
};

namespace Membership {
  export type Id = `m_${string}`;

  export namespace Id {
    export const is = (id: string | undefined | null): id is Id =>
      !!id?.startsWith("m_");
  }

  export type Status = "active" | "inactive";
  export type Plan = {
    id: Plan.Id;
    organizationId: Organization.Id;
    name: string;
    description: string;
    priceCents: number;
    status: Status;
    introOfferType?: Plan.IntroOfferType;
    renewalPeriod?: Plan.Period;
    classLimit?: number;
    classRefreshPeriod?: Plan.Period;
    guestPassLimit?: number;
    guestPassRefreshPeriod?: Plan.Period;
  };
  export namespace Plan {
    export type Id = `mp_${string}`;
    export namespace Id {
      export const is = (id: unknown | undefined): id is Id =>
        typeof id === "string" && !!id?.startsWith("mp_");
      export const zodSchema = (message = "Invalid membership plan id.") =>
        UZod.predicate(is, message);
    }

    export type Period = `${number} ${Period.Unit}`;

    export namespace Period {
      export type Unit = (typeof Period.Unit.list)[number];
      export namespace Unit {
        export const list = [
          "day",
          "week",
          "month",
          "year",
        ] as const satisfies Duration.Unit[];
      }
      export const is = (value: string): value is Period => {
        const [count, unit] = UString.split(value, " ");
        return Unit.list.includes(unit as any) && !Number.isNaN(+count);
      };
      export const zodEnforce: z.ZodType<Period, z.ZodTypeDef, Period> =
        UZod.enforce(is, "Invalid period.") as any;
    }

    export type IntroOfferType = (typeof IntroOfferType.list)[number];
    export namespace IntroOfferType {
      export const list = [
        "pre-booking",
        "pre-membership",
        "pre-self",
      ] as const;
    }

    export type Params = Omit<Plan, "id" | "organizationId" | "status">;

    export namespace Params {
      export type ToApi = Omit<FromApi, "id" | "organizationId" | "status">;
      export const denormalize = (params: Params): ToApi => {
        const [refreshPeriodAmount, refreshPeriodUnit] = UString.split(
          params.renewalPeriod || "",
          " ",
        );
        const [classLimitPeriodAmount, classLimitPeriodUnit] = UString.split(
          params.classRefreshPeriod || "",
          " ",
        );
        const [guestPassRefreshPeriodAmount, guestPassRefreshPeriodUnit] =
          UString.split(params.guestPassRefreshPeriod || "", " ");
        return {
          ...params,
          introOfferType:
            params.introOfferType &&
            Case.fromKebab(params.introOfferType).toSnake(),
          refreshPeriodAmount: +refreshPeriodAmount,
          refreshPeriodUnit: refreshPeriodUnit || "none",
          classLimitPeriodAmount: +classLimitPeriodAmount,
          classLimitPeriodUnit: classLimitPeriodUnit || "none",
          guestPassCount: params.guestPassLimit,
          guestPassPeriodAmount: +guestPassRefreshPeriodAmount,
          guestPassPeriodUnit: guestPassRefreshPeriodUnit || "none",
        };
      };
    }

    export type Supplement = {
      id: Plan.Id;
      numActives: number;
    };

    export type FromApi = UObject.Override<
      Omit<
        Plan,
        | "renewalPeriod"
        | "classRefreshPeriod"
        | "guestPassRefreshPeriod"
        | "guestPassLimit"
      >,
      {
        id: string;
        introOfferType?: Case.ToSnake<Case.FromKebab<Plan.IntroOfferType>>;
        refreshPeriodAmount: number;
        refreshPeriodUnit: Plan.Period.Unit | "none";
        classLimitPeriodAmount: number;
        classLimitPeriodUnit: Plan.Period.Unit | "none";
        guestPassCount?: number;
        guestPassPeriodAmount: number;
        guestPassPeriodUnit: Plan.Period.Unit | "none";
      }
    >;

    export const normalize = (plan: FromApi): Plan => ({
      ...plan,
      id: plan.id as Plan.Id,
      introOfferType:
        plan.introOfferType && Case.fromSnake(plan.introOfferType).toKebab(),
      renewalPeriod:
        plan.refreshPeriodUnit === "none"
          ? undefined
          : `${plan.refreshPeriodAmount} ${plan.refreshPeriodUnit}`,
      classLimit: plan.classLimit === 0 ? undefined : plan.classLimit,
      classRefreshPeriod:
        plan.classLimitPeriodUnit === "none"
          ? undefined
          : `${plan.classLimitPeriodAmount} ${plan.classLimitPeriodUnit}`,
      guestPassLimit: plan.guestPassCount,
      guestPassRefreshPeriod:
        plan.guestPassPeriodUnit === "none"
          ? undefined
          : `${plan.guestPassPeriodAmount} ${plan.guestPassPeriodUnit}`,
    });
  }
}

export default Membership;
